<template>
  <div>
    <rest-list
      v-if="isLoading || data[0]"
      ref="rest-list"
      :filterable="false"
      :payload="fetchEndpoint"
      :apply-filters-to-url="false"
      :empty-text="$t('Step_Report_Table_Text_NoReports')"
      :loading="isLoading"
      @dataLoaded="setData($event)"
      :after-fetch="() => (isLoading = false)"
    >
      <template>
        <rest-list-column
          :title="$t('Step_Report_Table_Column_Title')"
          prop="title"
        >
          <template #default="scope">
            <router-link
              :to="{
                name: 'UpdateCampaignReport',
                query: {
                  id: $route.query.id,
                  project_id: $route.query.project_id,
                  report_id: scope.row.id
                }
              }"
            >
              {{ scope.row.title }}
            </router-link>
          </template>
        </rest-list-column>
        <rest-list-column
          :title="$t('Step_Report_Table_Column_Created')"
          prop="create_time"
        >
          <template #default="scope">
            {{ dateFormatter(scope.row.create_time) }}
          </template>
        </rest-list-column>
        <rest-list-column
          :title="$t('Step_Report_Table_Column_Report_Type')"
          prop="report_type"
          :sortable="false"
        >
          <template #default="scope">
            {{ $t(analyticsReportTypeLabels[scope.row.report_type]) }}
          </template>
        </rest-list-column>
        <rest-list-column
          :title="$t('Step_Report_Table_Column_Status')"
          prop="active"
          :sortable="false"
        >
          <template #default="scope">
            <el-tag :type="scope.row.active ? 'success' : 'danger'">
              {{
                scope.row.active
                  ? $t('Step_Report_Table_Column_Status_Running')
                  : $t('Step_Report_Table_Column_Status_Stopped')
              }}
            </el-tag>
          </template>
        </rest-list-column>
        <rest-list-column
          :title="$t('Step_Report_Table_Column_Runs_Left')"
          prop="runs_left"
          :sortable="false"
        />
        <rest-list-column-actions>
          <template slot-scope="scope">
            <rest-list-action
              :title="$t('RestList_Action_Update')"
              :route="reportEditRoute(scope.row.id)"
              icon="el-icon-edit-outline"
            />
            <rest-list-action
              v-if="!scope.row.active"
              icon="el-icon-video-play"
              :title="$t('RestList_Action_Start')"
              :click="() => startReport(scope.row)"
            />
            <rest-list-action
              v-if="scope.row.active"
              icon="el-icon-video-pause"
              :title="$t('RestList_Action_Stop')"
              :click="() => stopReport(scope.row.id)"
            />
            <rest-list-action-delete
              :endpoint="rowDeleteEndpoint(scope.row.id)"
              :name="scope.row.title"
            />
          </template>
        </rest-list-column-actions>
      </template>
    </rest-list>
    <el-card v-else>
      <div class="flex-container">
        <icon glyph="analytics" size="50" style="color: #1d87c8" />
        <h2>
          {{ $t('CampaignAnalytics_Reports_Tab') }}
        </h2>
        <p>
          {{ $t('CampaignAnalytics_Reports_Tab_Description') }}
        </p>
      </div>
    </el-card>
  </div>
</template>

<script>
import RestListColumnActions from '@/common/components/rest/RestListColumnActions.vue'
import RestListAction from '@/common/components/rest/RestListAction.vue'
import {
  projectTimezoneDate,
  projectRoute,
  projectEndpoint,
  projectUrl
} from '@/core/helpers'
import RestList from '@/common/components/rest/RestList.vue'
import RestListColumn from '@/common/components/rest/RestListColumn.vue'
import {
  analyticsReportType,
  analyticsReportTypeLabels
} from '@/modules/campaign-manage/enum/AnalyticsReportType'
import RestApi from '@/core/services/RestApi'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete.vue'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'ReportsContent',

  components: {
    RestListActionDelete,
    RestListColumn,
    RestList,
    RestListAction,
    RestListColumnActions
  },

  props: {
    model: {
      type: Object
    }
  },

  data() {
    return {
      analyticsReportTypeLabels,
      data: [],
      isLoading: true
    }
  },

  methods: {
    setData(event) {
      this.data = event
    },

    reportEditRoute(reportId) {
      return projectRoute({
        name: 'UpdateCampaignReport',
        query: { id: this.$route.query.id, report_id: reportId }
      })
    },

    stopReport(reportId) {
      this.$msgbox({
        title: this.$t('Step_Report_Action_Stop_PopUp_Title'),
        message: this.$t('Step_Report_Action_Stop_PopUp_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('Step_Report_Action_Stop_PopUp_Btn_Stop'),
        cancelButtonText: this.$t('Step_Report_Action_Stop_PopUp_Btn_Cancel'),
        center: true
      }).then(() => {
        RestApi.patch(projectUrl(`campaign/reports/${reportId}`), {
          active: 0
        })
          .then(() => {
            this.$message({
              message: this.$t('RestList_StopSuccessMessage'),
              type: 'success'
            })
            this.$refs['rest-list'].refresh()
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      })
    },

    startReport(report) {
      if (
        !$hasFeature('journeyBuilderCampaignAnalytics') &&
        (report.report_type === analyticsReportType.journey_actions ||
          report.report_type === analyticsReportType.currently_waiting)
      ) {
        this.$message({
          message: this.$t('Step_Report_Action_Start_Error_FeatureFlag'),
          type: 'error'
        })

        return
      }
      if (report.runs_left === 0) {
        this.$message({
          message: this.$t('Step_Report_Action_Start_Error'),
          type: 'error'
        })

        return
      }

      RestApi.patch(projectUrl(`campaign/reports/${report.id}`), {
        active: 1
      })
        .then(() => {
          this.$message({
            message: this.$t('RestList_StartSuccessMessage'),
            type: 'success'
          })
          this.$refs['rest-list'].refresh()
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    dateFormatter(date) {
      return projectTimezoneDate(date).format('DD MMM YYYY HH:mm:ss')
    },

    rowDeleteEndpoint(reportId) {
      return projectEndpoint({
        method: 'delete',
        url: `campaign/reports/${reportId}`
      })
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'campaign/reports',
        params: {
          campaign_id: this.$route.query.id,
          sort: '-created_time'
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.flex-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  row-gap: 20px;
  padding: 20px 0;

  p {
    width: 470px;
    font-size: 16px;
    color: #7e8596;
  }

  h2 {
    font-size: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/analytics/ReportsContent.vue